import { useCallback, useMemo } from 'react';
import { styled } from 'goober';
import { Entity, EntityKey, EntityRelationType, EntityType } from '@common/api';
import { appColors, Z_INDEX } from '@base/useTheme';
import { Button } from '@components/button';
import { FilterMenu } from '@components/filter-menu/FilterMenu';
import { Close, Copy } from '@components/icons/FarseerIcons';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { useRelatedEntities } from '@data/entity/relation';
import { ENTITY_FILTERS } from '@data/entity/search';
import { AddTag } from '@features/tags/AddTag';
import { useI18nContext } from '@i18n/i18n-react';
import { useEntityContext } from '@providers/EntityProvider';

export interface FormulaFilter {
    tableGid: number;
    memberGids: number[];
}

export interface FormulaRule {
    filters: FormulaFilter[];
}

export interface CompoundFormulaBuilderProps {
    rules: FormulaRule[];
    onChange: (rules: FormulaRule[]) => void;
}

const RuleContainer = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid ${appColors.neutral.w200};
    border-radius: 8px;
    background: ${appColors.neutral.w50};
`;

const OrDivider = styled('div')`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${appColors.neutral.w400};
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: ${appColors.neutral.w200};
    }
`;

const AndLabel = styled('span')`
    color: ${appColors.neutral.w400};
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    padding: 0 8px;
`;

const getLabel = (e: Entity) => (e.meta.description ? `${e.meta.name} - ${e.meta.description}` : e.meta.name);
const getValue = (e: Entity) => e.id.toString();

/** Resolve an EntityKey (table or member) to { tableGid, memberGid }. */
function resolveTableAndMember(entity: Entity): { tableGid: number; memberGid: number | null } | null {
    if (entity.type === EntityType.DimensionTable) {
        return { tableGid: entity.id, memberGid: null };
    }
    if (entity.type === EntityType.DimensionMember) {
        const parentId = entity.relations?.parent?.key?.id;
        if (!parentId) return null;
        return { tableGid: parentId, memberGid: entity.id };
    }
    return null;
}

// --- FilterDropdown ---

interface FilterDropdownProps {
    tableKey: EntityKey;
    memberGids: number[];
    otherFilters: FormulaFilter[];
    onSelectMembers: (memberGids: number[]) => void;
    onRemove: () => void;
}

function FilterDropdown({ tableKey, memberGids, otherFilters, onSelectMembers, onRemove }: FilterDropdownProps) {
    const { LL } = useI18nContext();
    const { entity: table, relatedEntities: members } = useRelatedEntities(tableKey, EntityRelationType.Parent);

    const validMembers = useMemo(() => members.filter((m): m is Entity => m !== undefined), [members]);

    // Set of member IDs connected to selections in other filters (null = all available)
    const connectedIds = useMemo(() => {
        const otherSelected = new Set(otherFilters.flatMap((f) => f.memberGids));
        if (otherSelected.size === 0) return null;

        return new Set(
            validMembers
                .filter((m) => (m.links?.connections ?? []).some((c) => c.key && otherSelected.has(c.key.id)))
                .map((m) => m.id)
        );
    }, [validMembers, otherFilters]);

    const sortedMembers = useMemo(() => {
        if (!connectedIds) return validMembers;
        return [...validMembers].sort((a, b) => Number(connectedIds.has(b.id)) - Number(connectedIds.has(a.id)));
    }, [validMembers, connectedIds]);

    const title = useMemo(() => {
        const name = table?.meta.name ?? '';
        const count = memberGids.length;
        const total = validMembers.length;
        if (count === 0) return `${name} (0)`;
        if (count === total && total > 0) return `${name} (All ${total})`;
        if (count === 1) {
            const label = validMembers.find((m) => m.id === memberGids[0])?.meta.name ?? '1';
            return `${name} (${label.length > 12 ? label.substring(0, 15) + '...' : label})`;
        }
        return `${name} (${count})`;
    }, [table, memberGids, validMembers]);

    const selectedMembers = useMemo(
        () => validMembers.filter((m) => memberGids.includes(m.id)),
        [validMembers, memberGids]
    );

    const onSelect = useCallback(
        (selected: Entity[], isSelectAll: boolean) => {
            onSelectMembers(isSelectAll ? validMembers.map((m) => m.id) : selected.map((s) => s.id));
        },
        [validMembers, onSelectMembers]
    );

    return (
        <FilterMenu
            title={title}
            autoApply={false}
            searchable
            removeable
            multiselect
            canSelectAll
            options={sortedMembers}
            onRemove={onRemove}
            onSelect={onSelect}
            selected={selectedMembers}
            selectAll={memberGids.length === validMembers.length && validMembers.length > 0}
            getLabel={getLabel}
            getValue={getValue}
            getItemStyle={(m: Entity) =>
                connectedIds && !connectedIds.has(m.id) ? { opacity: 0.4 } : { opacity: 1.0 }
            }
            maxItems={10}
            searchLabel={LL.FILTER_BY()}
            removeLabel={LL.REMOVE_FROM_RULE()}
        >
            <FilterMenu.Trigger />
            <FilterMenu.Content zIndex={Z_INDEX.CONTEXT_MENU} />
        </FilterMenu>
    );
}

// --- CompoundFormulaBuilder ---

export function CompoundFormulaBuilder({ rules, onChange }: CompoundFormulaBuilderProps) {
    const { LL } = useI18nContext();
    const { getEntity } = useEntityContext();

    const addRule = useCallback(
        (key: EntityKey) => {
            const entity = getEntity(key);
            if (!entity) return;
            const resolved = resolveTableAndMember(entity);
            if (!resolved) return;
            const { tableGid, memberGid } = resolved;
            onChange([...rules, { filters: [{ tableGid, memberGids: memberGid ? [memberGid] : [] }] }]);
        },
        [rules, onChange, getEntity]
    );

    const addFilterToRule = useCallback(
        (ruleIndex: number, key: EntityKey) => {
            const entity = getEntity(key);
            if (!entity) return;
            const resolved = resolveTableAndMember(entity);
            if (!resolved) return;
            const { tableGid, memberGid } = resolved;

            const newRules = [...rules];
            const existing = newRules[ruleIndex].filters.findIndex((f) => f.tableGid === tableGid);

            if (existing >= 0) {
                if (memberGid && !newRules[ruleIndex].filters[existing].memberGids.includes(memberGid)) {
                    newRules[ruleIndex].filters[existing].memberGids.push(memberGid);
                    onChange(newRules);
                }
            } else {
                newRules[ruleIndex].filters.push({ tableGid, memberGids: memberGid ? [memberGid] : [] });
                onChange(newRules);
            }
        },
        [rules, onChange, getEntity]
    );

    const updateFilter = useCallback(
        (ruleIndex: number, filterIndex: number, memberGids: number[]) => {
            const newRules = [...rules];
            newRules[ruleIndex].filters[filterIndex].memberGids = memberGids;
            onChange(newRules);
        },
        [rules, onChange]
    );

    const removeFilter = useCallback(
        (ruleIndex: number, filterIndex: number) => {
            const newRules = [...rules];
            newRules[ruleIndex].filters.splice(filterIndex, 1);
            if (newRules[ruleIndex].filters.length === 0) newRules.splice(ruleIndex, 1);
            onChange(newRules);
        },
        [rules, onChange]
    );

    return (
        <FlexCol $gap={8} $align="start">
            {rules.map((rule, ruleIndex) => (
                <FlexCol key={ruleIndex} $gap={8} style={{ alignSelf: 'stretch' }}>
                    {ruleIndex > 0 && <OrDivider>OR</OrDivider>}
                    <RuleContainer>
                        <FlexRow $gap={4} $align="center" $justify="space-between">
                            <FlexRow $align="center" $gap={4} $wrap>
                                {rule.filters.map((filter, filterIndex) => (
                                    <FlexRow key={filter.tableGid} $align="center" $gap={4}>
                                        {filterIndex > 0 && <AndLabel>AND</AndLabel>}
                                        <FilterDropdown
                                            tableKey={{ id: filter.tableGid, type: EntityType.DimensionTable }}
                                            memberGids={filter.memberGids}
                                            otherFilters={rule.filters.filter((_, i) => i !== filterIndex)}
                                            onSelectMembers={(gids) => updateFilter(ruleIndex, filterIndex, gids)}
                                            onRemove={() => removeFilter(ruleIndex, filterIndex)}
                                        />
                                    </FlexRow>
                                ))}
                                <AddTag
                                    exclude={rule.filters.map((f) => ({
                                        id: f.tableGid,
                                        type: EntityType.DimensionTable,
                                    }))}
                                    entityFilter={ENTITY_FILTERS.dimension}
                                    onAdd={(key) => addFilterToRule(ruleIndex, key)}
                                    kind={LL.TABLE()}
                                />
                            </FlexRow>
                            <FlexRow $align="center" $gap={4}>
                                <TooltipWrapper label={LL.DUPLICATE_RULE()}>
                                    <Button
                                        $size="small"
                                        $type="quaternary"
                                        icon={<Copy />}
                                        onClick={() => {
                                            const newRules = [...rules];
                                            newRules.splice(
                                                ruleIndex + 1,
                                                0,
                                                JSON.parse(JSON.stringify(rules[ruleIndex]))
                                            );
                                            onChange(newRules);
                                        }}
                                    />
                                </TooltipWrapper>
                                <TooltipWrapper label={LL.REMOVE_RULE()}>
                                    <Button
                                        $size="small"
                                        $type="quaternary"
                                        icon={<Close />}
                                        onClick={() => onChange(rules.filter((_, i) => i !== ruleIndex))}
                                    />
                                </TooltipWrapper>
                            </FlexRow>
                        </FlexRow>
                    </RuleContainer>
                </FlexCol>
            ))}
            <AddTag
                entityFilter={ENTITY_FILTERS.dimension}
                onAdd={addRule}
                kind={LL.TABLE()}
                addLabel={LL.NEW_RULE()}
            />
        </FlexCol>
    );
}
